import { createSelector, createSlice } from '@reduxjs/toolkit';

import {
  AddressModel,
  CartPayModeModel,
  ExproModel,
  ExproOrderModel,
  ItemWineBoxExproModel,
  ProcessExproOrderRequestModel,
  TopChronoDataModel,
  DeliverySlotDataModel
} from '@/models';
import { BrowserInfo, DelayedDeliveryDetails, SlicePayload } from '@/types';
import { RootState } from '@/redux/create-store';
import { updateUserAddressSuccess } from '@/redux/slices/user.slice';
import { isObjectEmpty } from '@/utils/comparisonUtils';

export type Step = {
  step: number;
  title: string;
  isCompleted: boolean;
  isActive: boolean;
  isValid?: boolean;
}

export type ExproState = {
  exproOrder: ExproOrderModel;
  lastExproOrder: ExproOrderModel;
  validateSelectedAddress: boolean;
  addressToEdit: AddressModel;
  addressFormModalVisible: boolean;
  isFromExpro: boolean;
  addressList: AddressModel[];
  addressListModalVisible: boolean;
  selectedAddress: AddressModel | null;
  exproWineBoxItems: ItemWineBoxExproModel[];
  exproWineBoxModalVisible: boolean;
  detailsExproModalVisible: boolean;
  selectedExpro: ExproModel;
  payModeList: CartPayModeModel[];
  topChronoDataExpro?: TopChronoDataModel;
  deliverySlotDataExpro?: Partial<DeliverySlotDataModel>;
  delayedDeliveryDetails: { [idShippingMode: string]: DelayedDeliveryDetails };
  adyenSubmit: boolean;
  checkoutSteps: Step[];
}

export const initialState: ExproState = {
  exproOrder: {} as ExproOrderModel,
  lastExproOrder: {} as ExproOrderModel,
  validateSelectedAddress: false,
  addressToEdit: {} as AddressModel,
  addressFormModalVisible: false,
  isFromExpro: false,
  addressList: [],
  addressListModalVisible: false,
  selectedAddress: null,
  exproWineBoxItems: [],
  exproWineBoxModalVisible: false,
  detailsExproModalVisible: false,
  selectedExpro: {} as ExproModel,
  payModeList: [],
  topChronoDataExpro: {} as any,
  deliverySlotDataExpro: {} as any,
  delayedDeliveryDetails: {},
  adyenSubmit: false,
  checkoutSteps: [
    {
      step: 1,
      title: 'checkout:shippingAddress.title',
      isCompleted: false,
      isActive: true,
      isValid: false
    },
    {
      step: 2,
      title: 'checkout:shipMode.title',
      isCompleted: false,
      isActive: false,
      isValid: true
    },
    {
      step: 3,
      title: 'checkout:payMode.title',
      isCompleted: false,
      isActive: false,
      isValid: false
    }
  ],
};

const expro = createSlice({
  name: 'expro',
  initialState,
  reducers: {
    setExproOrder: (state, action: SlicePayload<{exproOrder: ExproOrderModel}>) => {
      state.exproOrder = action.payload.exproOrder;
      state.checkoutSteps[0].isValid = !isObjectEmpty(state.exproOrder.shipping);
    },

    setLastExproOrder: (state, action: SlicePayload<{lastExproOrder: ExproOrderModel}>) => {
      state.lastExproOrder = action.payload.lastExproOrder;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    updateExproOrder: (_state, _action: SlicePayload<{exproOrder: ExproOrderModel, updateStep?: any}>) => {
    },

    updateExproOrderAddress: (state, action: SlicePayload<{shipping: AddressModel}>) => {
      state.exproOrder = {
        ...state.exproOrder,
        shipping: action.payload.shipping,
        billing: action.payload.shipping
      };
    },

    updateExproOrderPaymentMode: (state, action: SlicePayload<{payMode: CartPayModeModel}>) => {
      state.exproOrder = {
        ...state.exproOrder,
        paymentMode: action.payload.payMode
      };
    },

    setFingerprint: (state, action: SlicePayload<{fingerprint: string}>) => {
      state.exproOrder.paymentMode.fingerprint = action.payload.fingerprint;
    },

    setBrowserInfos: (state, action: SlicePayload<{browserInfos: BrowserInfo}>) => {
      state.exproOrder.browserInfo = action.payload.browserInfos;
    },

    setValidateSelectedAddress: (state, action: SlicePayload<{validateSelectedAddress: boolean}>) => {
      state.validateSelectedAddress = action.payload.validateSelectedAddress;
    },

    toggleAddressFormModal: (
      state,
      action: SlicePayload<{ opened: boolean, isFromExpro?: boolean, addressToEdit?: AddressModel }>
    ) => {
      state.addressToEdit = action.payload.addressToEdit || {} as AddressModel;
      state.addressFormModalVisible = action.payload.opened;
      state.isFromExpro = action.payload.isFromExpro;
    },

    toggleAddressListModal: (state, action: SlicePayload<{opened: boolean, addresses: AddressModel[]}>) => {
      state.addressList = action.payload.addresses;
      state.addressListModalVisible = action.payload.opened;
    },

    setSelectedAddress: (state, action: SlicePayload<{selectedAddress: AddressModel}>) => {
      state.selectedAddress = action.payload.selectedAddress;
    },

    toggleDetailsExproModal: (state, action: SlicePayload<{ opened: boolean, selectedExpro: ExproModel }>) => {
      state.detailsExproModalVisible = action.payload.opened;
      state.selectedExpro = action.payload.selectedExpro;
    },

    toggleExproWineBoxModal: (state, action: SlicePayload<{opened: boolean, exproWineBoxItems: ItemWineBoxExproModel[]}>) => {
      state.exproWineBoxItems = action.payload.exproWineBoxItems;
      state.exproWineBoxModalVisible = action.payload.opened;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    getPayModes: (_state, _action: SlicePayload<{ storeId: string, isApplePayEnabled: boolean }>) => {
    },

    setPayModes: (state, action: SlicePayload<{payModes: CartPayModeModel[]}>) => {
      state.payModeList = action.payload.payModes;
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    payExproOrder: (_state, _action: SlicePayload<{processExproOrderRequest: ProcessExproOrderRequestModel, t: any}>) => {
    },

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    managePayExproOrderResponse: (_state, _action: SlicePayload<{response: any, t: any}>) => {
    },

    // TOP CHRONO
    updateTopChronoDataExpro: (state, action: SlicePayload<{ data: TopChronoDataModel }>) => {
      state.topChronoDataExpro = action.payload.data;
    },

    // DELIVERY SLOT
    updateDeliverySlotDataExpro: (state, action: SlicePayload<{ data: Partial<DeliverySlotDataModel> }>) => {
      state.deliverySlotDataExpro = action.payload.data;
    },

    // LIVRAISON DIFFEREE
    setDelayedDeliveryDetailsExpro: (
      state,
      action: SlicePayload<{ delayedDeliveryDetails: { [idShippingMode: string]: DelayedDeliveryDetails }
    }>) => {
      state.delayedDeliveryDetails = action.payload.delayedDeliveryDetails;
    },

    setAdyenSubmit: (state, action: SlicePayload<{submit: boolean}>) => {
      state.adyenSubmit = action.payload.submit;
    },

    initializeCheckoutStepTitles: (state, action: SlicePayload<{ titles: string[] }>) => {
      action.payload.titles.forEach((title, index) => {
        if (state.checkoutSteps[index]) {
          state.checkoutSteps[index].title = title;
        }
      });
      if (state.checkoutSteps[0]) {
        state.checkoutSteps[0].isValid = !!state.exproOrder.shipping;
      }
    },

    completeCheckoutStep: (
      state,
      action: SlicePayload<{
        payload: 'firstStep' | 'inBetweenStep' | 'lastStep';
        stepNumber: number;
      }>
    ) => {
      const { payload, stepNumber } = action.payload;

      state.checkoutSteps.forEach((step, index) => {
        if (payload === 'firstStep') {
          step.isCompleted = index === 0;
          step.isActive = index === 1;
          if (index === 0) {
            step.isValid = !!state.exproOrder.shipping;
          }
        } else if (payload === 'inBetweenStep') {
          step.isCompleted = index <= stepNumber - 1;
          step.isActive = index === stepNumber;
        } else if (payload === 'lastStep') {
          step.isCompleted = true;
          step.isActive = false;
        }
      });
    },

    uncompleteCheckoutStep: (
      state,
      action: SlicePayload<{
        payload: 'firstStep' | 'inBetweenStep' | 'lastStep';
        stepNumber: number;
      }>
    ) => {
      const { payload, stepNumber } = action.payload;
      const lastStep = state.checkoutSteps.length;

      state.checkoutSteps.forEach((step, index) => {
        if (payload === 'firstStep') {
          step.isCompleted = false;
          step.isActive = index === stepNumber - 1;
          if (index === 0) {
            step.isValid = !!state.exproOrder.shipping;
          }
        } else if (payload === 'inBetweenStep') {
          step.isCompleted = index < stepNumber - 1;
          step.isActive = index === stepNumber - 1;
        } else if (payload === 'lastStep') {
          step.isCompleted = index < lastStep - 1;
          step.isActive = index === lastStep - 1;
        }
      });
    },

    validateCheckoutStep: (
      state,
      action: SlicePayload<{
        stepNumber: number;
        valid: boolean;
      }>
    ) => {
      const step = state.checkoutSteps.find((s) => s.step === action.payload.stepNumber);
      if (step) {
        step.isValid = action.payload.valid;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateUserAddressSuccess, (state) => {
      state.addressFormModalVisible = false;
      state.addressToEdit = {} as AddressModel;
    });
  }
});

export const {
  setExproOrder, setLastExproOrder, updateExproOrder,
  updateExproOrderAddress, updateExproOrderPaymentMode,
  setFingerprint, setBrowserInfos,
  setValidateSelectedAddress,
  toggleAddressFormModal,
  toggleAddressListModal,
  setSelectedAddress,
  toggleDetailsExproModal,
  toggleExproWineBoxModal,
  getPayModes, setPayModes,
  payExproOrder,
  managePayExproOrderResponse,
  updateTopChronoDataExpro,
  updateDeliverySlotDataExpro,
  setDelayedDeliveryDetailsExpro,
  setAdyenSubmit,
  initializeCheckoutStepTitles,
  completeCheckoutStep,
  uncompleteCheckoutStep,
  validateCheckoutStep
} = expro.actions;

export default expro.reducer;

const selectExproState = (state: RootState) => state.expro;

export const selectAddressFormModalInfos = createSelector(
  [selectExproState],
  (expro) => ({
    addressToEdit: expro.addressToEdit,
    addressFormModalVisible: expro.addressFormModalVisible,
    isFromExpro: expro.isFromExpro
  })
);

export const selectCheckoutSteps = createSelector(
  [selectExproState],
  (expro) => expro.checkoutSteps
);

export const selectLastStep = createSelector(
  [selectExproState],
  (expro) => expro.checkoutSteps.length
);
